import { useMemo, useState } from "react";
import styled from "styled-components";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Button } from "../Button";
import { Paragraph } from "../Paragraph";
import { Toggle } from "../Toggle";
import { useDivisions } from "../../data/useDivisions";
import { useTeams } from "../../data/useTeams";
import { Team } from "../../types/Team";
import { AccessLevel, NewShare, Share } from "./types";

const LevelCard = styled.button<{ isSelected: boolean }>`
  ${({ isSelected, theme: { colors, radii, space } }) => `
    display: flex;
    gap: 12px;
    width: 100%;
    text-align: left;
    padding: 12px 14px;
    box-sizing: border-box;
    border-radius: ${radii[0]};
    background: ${colors.white};
    font-family: "Inter", sans-serif;
    cursor: pointer;
    border: 1px solid ${isSelected ? colors.blue.primary : colors.gray[30]};
    ${isSelected ? `box-shadow: 0 0 0 3px ${colors.blue[50]};` : ""}
  `}
`;

const RadioDot = styled.span<{ isSelected: boolean }>`
  ${({ isSelected, theme: { colors } }) => `
    width: 16px;
    height: 16px;
    margin-top: 2px;
    flex-shrink: 0;
    box-sizing: border-box;
    border-radius: 50%;
    border: ${isSelected ? `5px solid ${colors.blue.primary}` : `1px solid ${colors.gray[40]}`};
  `}
`;

const EmptyNote = styled.p`
  ${({ theme: { colors, radii, space, fontSizes } }) => `
    margin: 0;
    padding: 12px ${space[2]};
    border: 1px dashed ${colors.gray[30]};
    border-radius: ${radii[0]};
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[1]};
    color: ${colors.gray[50]};
  `}
`;

/** Same compact toggle the top-bar Division/Team selector uses. */
const SmallToggle = (props: React.ComponentProps<typeof Toggle>) => (
  <Box style={{ transform: "scale(0.7)", transformOrigin: "right center" }}>
    <Toggle {...props} />
  </Box>
);

const Tag = ({ children }: { children: React.ReactNode }) => (
  <Paragraph as="span" m={0} ml={1} fontSize={0} color="gray.50">
    {children}
  </Paragraph>
);

interface Props {
  primaryDivisionId?: number;
  primaryTeamId?: number;
  /** "Lake Pvt Ltd · Default" — used in the View only explanation. */
  primaryLabel: string;
  existingShares: Share[];
  noun: string;
  /** Show the View only / Can edit choice. Off until the API stores levels. */
  supportsLevels?: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (shares: NewShare[]) => void;
}

/**
 * Inline "add a share" form at the top of the Shared with list: every active
 * Division with its Teams nested under it, each with a toggle. A Team belongs
 * to its Division, so the two cascade:
 * - Division on → all its Teams on; any of them can then be switched off
 *   (a Division can be shared with no Teams).
 * - Team on → its Division is shared too.
 * - Division off → its Teams go with it.
 * The primary and already-shared Divisions/Teams show as on and locked; Teams
 * inside a locked Division can still be added. Mounted only while open, so
 * the lists are fetched on demand.
 */
export const AddShareForm = ({
  primaryDivisionId,
  primaryTeamId,
  primaryLabel,
  existingShares,
  noun,
  supportsLevels = false,
  isSubmitting,
  onCancel,
  onSubmit,
}: Props) => {
  const [newDivisionIds, setNewDivisionIds] = useState<number[]>([]);
  const [newTeamIds, setNewTeamIds] = useState<number[]>([]);
  const [level, setLevel] = useState<AccessLevel>("view");

  const divisionsQuery = useDivisions();
  const teamsQuery = useTeams();

  const divisions = useMemo(
    () => (divisionsQuery.data?.data ?? []).filter((d) => d.is_active),
    [divisionsQuery.data],
  );
  const teamsByDivision = useMemo(() => {
    const map = new Map<number, Team[]>();
    (teamsQuery.data?.data ?? [])
      .filter((t) => t.is_active)
      .forEach((t) => map.set(t.division, [...(map.get(t.division) ?? []), t]));
    return map;
  }, [teamsQuery.data]);
  const teamsIn = (divisionId: number) => teamsByDivision.get(divisionId) ?? [];

  const sharedDivisionIds = new Set(
    existingShares.filter((s) => s.kind === "division").map((s) => s.id),
  );
  const sharedTeamIds = new Set(
    existingShares.filter((s) => s.kind === "team").map((s) => s.id),
  );
  const isLockedDivision = (id: number) =>
    id === primaryDivisionId || sharedDivisionIds.has(id);
  const isLockedTeam = (id: number) =>
    id === primaryTeamId || sharedTeamIds.has(id);

  const addableTeamIds = (divisionId: number) =>
    teamsIn(divisionId)
      .map((t) => t.id)
      .filter((id) => !isLockedTeam(id));

  const toggleDivision = (divisionId: number, checked: boolean) => {
    const teamIds = addableTeamIds(divisionId);
    if (checked) {
      setNewDivisionIds((ids) => [...ids, divisionId]);
      setNewTeamIds((ids) => Array.from(new Set([...ids, ...teamIds])));
    } else {
      setNewDivisionIds((ids) => ids.filter((id) => id !== divisionId));
      setNewTeamIds((ids) => ids.filter((id) => !teamIds.includes(id)));
    }
  };

  const toggleTeam = (team: Team, checked: boolean) => {
    if (checked) {
      setNewTeamIds((ids) => [...ids, team.id]);
      if (!isLockedDivision(team.division)) {
        setNewDivisionIds((ids) =>
          ids.includes(team.division) ? ids : [...ids, team.division],
        );
      }
    } else {
      // The Division stays shared — it can be shared with no Teams.
      setNewTeamIds((ids) => ids.filter((id) => id !== team.id));
    }
  };

  const isLoading = divisionsQuery.isLoading || teamsQuery.isLoading;
  const everythingShared =
    !isLoading &&
    divisions.every(
      (d) => isLockedDivision(d.id) && addableTeamIds(d.id).length === 0,
    );
  const hasChanges = newDivisionIds.length > 0 || newTeamIds.length > 0;

  const handleSubmit = () => {
    const withLevel = supportsLevels ? { level } : {};
    onSubmit([
      ...newDivisionIds.map((id) => ({
        kind: "division" as const,
        id,
        ...withLevel,
      })),
      ...newTeamIds.map((id) => ({ kind: "team" as const, id, ...withLevel })),
    ]);
  };

  const lockedTag = (isPrimary: boolean) => (
    <Tag>· {isPrimary ? "Primary" : "Already shared"}</Tag>
  );

  return (
    <Box
      border="1px solid"
      borderColor="blue.20"
      borderRadius={0}
      p={2}
      display="flex"
      flexDirection="column"
      gap="14"
    >
      {isLoading ? (
        <Paragraph m={0} fontSize={1} color="gray.50">
          Loading divisions and teams...
        </Paragraph>
      ) : everythingShared ? (
        <EmptyNote>
          This {noun} is already shared with every division and team.
        </EmptyNote>
      ) : (
        <Box maxHeight="320px" overflowY="auto">
          {divisions.map((division) => {
            const divisionLocked = isLockedDivision(division.id);
            const teams = teamsIn(division.id);
            return (
              <Box
                key={division.id}
                borderBottom="1px solid"
                borderBottomColor="gray.10"
              >
                <Flex justifyContent="space-between" alignItems="center" px={1}>
                  <Paragraph m={0} fontSize={1} fontWeight="bold">
                    {division.display_name}
                    {divisionLocked &&
                      lockedTag(division.id === primaryDivisionId)}
                  </Paragraph>
                  <SmallToggle
                    isChecked={
                      divisionLocked || newDivisionIds.includes(division.id)
                    }
                    isDisabled={divisionLocked || isSubmitting}
                    onChange={(e) =>
                      toggleDivision(division.id, e.target.checked)
                    }
                  />
                </Flex>
                {teams.map((team) => {
                  const teamLocked = isLockedTeam(team.id);
                  return (
                    <Flex
                      key={team.id}
                      justifyContent="space-between"
                      alignItems="center"
                      pl={3}
                      pr={1}
                    >
                      <Paragraph m={0} fontSize={1} color="gray.60">
                        {team.display_name}
                        {teamLocked && lockedTag(team.id === primaryTeamId)}
                      </Paragraph>
                      <SmallToggle
                        isChecked={teamLocked || newTeamIds.includes(team.id)}
                        isDisabled={teamLocked || isSubmitting}
                        onChange={(e) => toggleTeam(team, e.target.checked)}
                      />
                    </Flex>
                  );
                })}
                {teams.length === 0 && (
                  <Paragraph m={0} pl={3} pb={1} fontSize={1} color="gray.50">
                    No teams in this division.
                  </Paragraph>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Paragraph m={0} fontSize={0} color="gray.50" lineHeight={1.5}>
        Turning on a division shares it with all its teams; switch off any you
        don't want. Turning on a team shares its division too.
      </Paragraph>

      {supportsLevels && (
      <>
      <LevelCard
        type="button"
        isSelected={level === "view"}
        onClick={() => setLevel("view")}
      >
        <RadioDot isSelected={level === "view"} />
        <Box>
          <Flex gap="6" alignItems="center">
            <Paragraph m={0} fontSize={1} fontWeight={600}>
              View only
            </Paragraph>
            <Paragraph
              m={0}
              px={1}
              fontSize={0}
              fontWeight={500}
              color="gray.60"
              bg="background"
              borderRadius={0}
            >
              Default
            </Paragraph>
          </Flex>
          <Paragraph m={0} mt={0} fontSize={1} color="gray.60" lineHeight={1.5}>
            Sees the whole {noun} but can't action it. Changes are made by, or
            routed through, {primaryLabel}.
          </Paragraph>
        </Box>
      </LevelCard>

      <LevelCard
        type="button"
        isSelected={level === "edit"}
        onClick={() => setLevel("edit")}
      >
        <RadioDot isSelected={level === "edit"} />
        <Box>
          <Paragraph m={0} fontSize={1} fontWeight={600}>
            Can edit
          </Paragraph>
          <Paragraph m={0} mt={0} fontSize={1} color="gray.60" lineHeight={1.5}>
            Can work the {noun} alongside the primary: update, action checks and
            resolve matches.
          </Paragraph>
        </Box>
      </LevelCard>
      </>
      )}

      <Flex justifyContent="flex-end" gap="8">
        <Button variant="grayText" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          isDisabled={!hasChanges || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? "Sharing..." : "Share"}
        </Button>
      </Flex>
    </Box>
  );
};
